import { memo, useEffect, useMemo, useRef, useState } from "react";
import "fullscreen-polyfill";
import Hls from "hls.js";
import { isFunction, uniqueId } from "lodash";
import Plyr from "plyr";
import type { PlyrEvent } from "plyr";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { MediaProgress } from "@/react/types/MediaProgress";
import { MediaElement } from "./MediaElement";
import { useHls } from "./useHlsPlyr";
import "./plyr.scss";

export interface MediaPlayerProps {
  autoplay?: boolean;
  captionsUrl?: string;
  contentType: string;
  downloadUrl?: string;
  isDownloadable: boolean;
  menuPosition?: "top" | "bottom";
  onEnded?: () => void;
  onProgress?: (progress: MediaProgress) => void;
  onTimeUpdate?: (currentTime: number) => void;
  playerRef?: any;
  poster?: string;
  preload?: "none" | "metadata" | "auto";
  signedId: string;
  src: string;
  startTime?: number;
}

export const MediaPlayer = memo(
  ({
    captionsUrl,
    contentType,
    downloadUrl,
    isDownloadable = false,
    onTimeUpdate,
    onEnded,
    playerRef = null,
    poster,
    src,
    startTime,
    preload = "none",
    onProgress,
    menuPosition = "top",
    signedId,
  }: MediaPlayerProps) => {
    const [renderKey, setRenderKey] = useState(uniqueId("plyr-container-"));
    const playerId = useRef(uniqueId("plyr-"));
    const plyrRef = useRef<Plyr | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isHlsSupported = Hls.isSupported();
    const isHls = src.endsWith(".m3u8");
    const isHlsEnabled = isHls && isHlsSupported;
    const isHlsAutoStartLoadEnabled = preload !== "none";

    const { quality, startLoad } = useHls({
      id: playerId.current,
      renderKey,
      src,
      enabled: isHlsEnabled,
      autoStartLoadEnabled: isHlsAutoStartLoadEnabled,
    });

    const playerOptions = useMemo(
      () => ({
        controls: [
          "play-large",
          "play",
          "progress",
          "current-time",
          "mute",
          "volume",
          ...(captionsUrl ? ["captions"] : []),
          "settings",
          "pip",
          "fullscreen",
          ...(isDownloadable ? ["download"] : []),
        ],
        urls: {
          download: downloadUrl,
        },
        speed: {
          selected: 1,
          options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
        },
        ...(quality ? { quality } : {}),
      }),
      [captionsUrl, downloadUrl, quality, isDownloadable],
    );

    useEffect(
      () => () => {
        plyrRef.current?.destroy();
        setRenderKey(uniqueId("plyr-container-"));
      },
      [playerOptions],
    );

    useEffect(() => {
      plyrRef.current = new Plyr(`#${playerId.current}`, playerOptions);
      if (playerRef) {
        playerRef.current = {
          plyr: plyrRef.current,
        };
      }
    }, [playerOptions, renderKey, playerRef]);

    const setStartTime = () => {
      if (plyrRef.current && startTime) {
        plyrRef.current.currentTime = startTime;
      }
    };

    const resetVideoProgress = () => {
      if (plyrRef.current && isFunction(onEnded)) {
        onEnded();
      }
    };

    const handleOnProgress = isFunction(onProgress)
      ? (event: PlyrEvent) => {
          onProgress({
            currentTime: event.detail.plyr.currentTime,
            duration: event.detail.plyr.duration,
          });
        }
      : null;

    // Set the start time of the video on load
    useEffect(() => {
      if (startTime && plyrRef.current) {
        plyrRef.current.once("canplay", setStartTime);
      }

      if (plyrRef.current) {
        plyrRef.current.once("ended", resetVideoProgress);
        if (handleOnProgress) {
          plyrRef.current.on("timeupdate", handleOnProgress);
        }
      }

      return () => {
        plyrRef.current?.off("canplay", setStartTime);
        if (handleOnProgress) {
          plyrRef.current?.off("timeupdate", handleOnProgress);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- plyrRef.current is necessary here since it may change
    }, [plyrRef.current]);

    useEffect(() => {
      if (!startTime || !plyrRef.current) return;

      plyrRef.current.once("play", setStartTime);

      return () => plyrRef.current?.off("play", setStartTime);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- plyrRef.current is necessary here since it may change
    }, [plyrRef.current]);

    useEffect(() => {
      if (!plyrRef.current || !isHlsEnabled || isHlsAutoStartLoadEnabled) {
        return;
      }

      plyrRef.current.once("play", startLoad);

      return () => plyrRef.current?.off("play", startLoad);
    }, [renderKey, isHlsEnabled, isHlsAutoStartLoadEnabled, startLoad]);

    useEffect(() => {
      const handleOnTimeUpdate = () => {
        if (plyrRef.current && onTimeUpdate) {
          onTimeUpdate(plyrRef.current.currentTime);
        }
      };

      if (onTimeUpdate) {
        plyrRef.current?.on("timeupdate", handleOnTimeUpdate);
      }

      return () => {
        if (onTimeUpdate) {
          plyrRef.current?.off("timeupdate", handleOnTimeUpdate);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- plyrRef.current is necessary here since it may change
    }, [onTimeUpdate, plyrRef.current]);

    useEffect(() => {
      if (plyrRef.current) {
        const handleEnterFullscreen = () => setIsFullscreen(true);
        const handleExitFullscreen = () => setIsFullscreen(false);

        plyrRef.current.on("enterfullscreen", handleEnterFullscreen);
        plyrRef.current.on("exitfullscreen", handleExitFullscreen);

        return () => {
          plyrRef.current?.off("enterfullscreen", handleEnterFullscreen);
          plyrRef.current?.off("exitfullscreen", handleExitFullscreen);
        };
      }
      return undefined;
    }, [plyrRef.current]);

    useEffect(() => {
      if (!plyrRef.current || !poster) return;

      plyrRef.current.poster = poster;
    }, [poster]);

    return (
      <div
        key={renderKey}
        className={classNames("plyr-container", {
          "plyr-container--fullscreen": isFullscreen,
          "plyr-container__menu--bottom": menuPosition === "bottom",
        })}
      >
        <MediaElement
          id={playerId.current}
          contentType={contentType}
          poster={poster}
          preload={preload}
          signedId={signedId}
        >
          {!isHlsEnabled && <source src={src} type={contentType} />}
          {captionsUrl && (
            <track
              kind="captions"
              label={t("default")}
              src={captionsUrl}
              srcLang="en"
            />
          )}
        </MediaElement>
      </div>
    );
  },
);

MediaPlayer.displayName = "MediaPlayer";
